import React from 'react';
import { Link } from 'react-router-dom';

/**
 * RmsBreadcrumb
 * Left-aligned breadcrumb for RMS post-account forms.
 *
 * Business rules:
 * - Only used on post-account forms where users can navigate back to an Overview page.
 * - Renders "Overview / Current Form" with "Overview" as a clickable link and the
 *   current form title as a non-clickable terminal crumb.
 *
 * Design notes:
 * - Keep the visual weight light to avoid competing with the form header title.
 * - Use compact spacing so it fits in the same row area as the brand/logo would.
 * - When an item has an `icon` prop, renders an expandable button that shows just
 *   the icon normally and smoothly expands on hover to reveal the label text.
 */
export default function RmsBreadcrumb({ items = [] }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <nav aria-label="Breadcrumb" className="mb-4">
      <ol className="flex items-center gap-2 text-sm text-gray-600">
        {items.map((it, idx) => {
          const isLast = idx === items.length - 1;
          return (
            <li key={`${it.label || idx}`} className="flex items-center gap-2">
              {isLast && !it.to && !it.onClick ? (
                <span className="px-1 font-semibold text-gray-900" aria-current="page">
                  {it.label}
                </span>
              ) : it.icon ? (
                // Expandable icon button: shows icon only, expands on hover to reveal label
                <ExpandableIconButton
                  icon={it.icon}
                  label={it.label}
                  onClick={it.onClick}
                  to={it.to}
                />
              ) : it.to ? (
                <Link to={it.to} className="px-1 hover:underline text-primary">
                  {it.label}
                </Link>
              ) : it.onClick ? (
                <button
                  type="button"
                  onClick={it.onClick}
                  className="px-1 hover:underline text-primary"
                >
                  {it.label}
                </button>
              ) : (
                <span className="px-1">{it.label}</span>
              )}
              {!isLast && <span className="text-gray-400">/</span>}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * ExpandableIconButton
 * A button that displays just an icon normally, but smoothly expands on hover
 * to reveal the full label text. Used for "Back to Overview" navigation.
 *
 * Design notes:
 * - Uses CSS transitions for smooth width and padding expansion
 * - Icon is always visible; label fades in and slides into view on hover
 * - Maintains a pill/rounded shape throughout the transition
 */
function ExpandableIconButton({ icon: Icon, label, onClick, to }) {
  const baseClasses = `
    group
    inline-flex items-center gap-0
    px-2 py-1.5
    rounded-full
    bg-gray-100 hover:bg-primary/10
    text-primary
    transition-all duration-300 ease-out
    hover:gap-2 hover:px-3
    focus:outline-none focus:ring-2 focus:ring-primary/30
  `;

  const iconClasses = "w-5 h-5 flex-shrink-0";
  
  const labelClasses = `
    max-w-0 overflow-hidden whitespace-nowrap
    opacity-0
    group-hover:max-w-[150px] group-hover:opacity-100
    transition-all duration-300 ease-out
    text-sm font-medium
  `;

  // Render as Link if `to` is provided, otherwise as button
  if (to) {
    return (
      <Link to={to} className={baseClasses} aria-label={label}>
        <Icon className={iconClasses} aria-hidden="true" />
        <span className={labelClasses}>{label}</span>
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={baseClasses}
      aria-label={label}
    >
      <Icon className={iconClasses} aria-hidden="true" />
      <span className={labelClasses}>{label}</span>
    </button>
  );
}
